/**
 * The studio's left column: every module the reader may open, core first.
 * The surface says what an empty studio means, so the list is never empty.
 *
 * @module components/studio/ModuleList
 */

import React from "react";

import { creditsLabel } from "@/components/shared/credits";
import { Badge, countLabel, MasterGroup, MasterItem, MasterList } from "@/components/ui";
import type { Module } from "@/types";

export interface ModuleListProps {
  modules: readonly Module[];
  activeModuleId: string | undefined;
  /** `${base}/studio`, which each entry hangs off. */
  base: string;
  /** The tab an entry opens on, so switching module keeps the reader's place. */
  tab: string;
}

function group(modules: readonly Module[]): { title: string; modules: readonly Module[] }[] {
  // A provisional module's elective status is not known yet: grouping it under
  // "Core" would state a fact the cold-load fallback never had.
  const known = modules.filter((module) => !module.provisional);
  const provisional = modules.filter((module) => module.provisional);
  const core = known.filter((module) => !module.isElective);
  const electives = known.filter((module) => module.isElective);
  return [
    { title: "Core", modules: core },
    { title: "Electives", modules: electives },
    { title: "Modules", modules: provisional },
  ].filter((entry) => entry.modules.length > 0);
}

export function ModuleList({
  modules,
  activeModuleId,
  base,
  tab,
}: ModuleListProps): React.ReactElement {
  return (
    <nav aria-label="Modules" data-testid="studio-module-list">
      {group(modules).map((section) => (
        <MasterGroup key={section.title} title={section.title}>
          <MasterList>
            {section.modules.map((module) => (
              <MasterItem
                key={module.id}
                to={`${base}/${module.id}/${tab}`}
                selected={module.id === activeModuleId}
                title={`${module.code ? `${module.code} — ` : ""}${module.title || "Untitled module"}`}
                data-testid={`studio-module-${module.id}`}
              >
                {module.provisional || module.credits ? (
                  // Add module stores zero, and "0" beside a title reads as a
                  // decision somebody made.
                  <Badge
                    tone="neutral"
                    aria-label={
                      module.provisional ? "credits not yet known" : creditsLabel(module.credits)
                    }
                  >
                    {module.provisional ? "—" : countLabel(Number(module.credits), "credit")}
                  </Badge>
                ) : null}
              </MasterItem>
            ))}
          </MasterList>
        </MasterGroup>
      ))}
    </nav>
  );
}

export default ModuleList;
